(function () {
  const SAMPLE_PRODUCT_ID = 15504923656567;
  const SAMPLE_PROPERTY = "_xmiles_free_sample";
  const OFFER_PROPERTY = "_sample_offer";

  function getCartRoot(config) {
    const shopifyRoot =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    const root = shopifyRoot || config.rootUrl || "/";
    return root.endsWith("/") ? root : `${root}/`;
  }

  function endpoint(root, path) {
    return `${root}${path}`;
  }

  function formatMoney(value, format) {
    const cents = Number(value || 0);
    const amount = Math.floor(cents / 100);
    const decimal = String(Math.abs(cents % 100)).padStart(2, "0");
    const withDecimals = `${amount}.${decimal}`;
    const withComma = `${amount},${decimal}`;

    return (format || "\u00a3{{amount}}")
      .replace("{{amount}}", withDecimals)
      .replace("{{amount_no_decimals}}", String(amount))
      .replace("{{amount_with_comma_separator}}", withComma)
      .replace("{{amount_no_decimals_with_comma_separator}}", String(amount));
  }

  function parseSampleVariantTitle(variantTitle, fallbackTitle) {
    const parts = String(variantTitle || "")
      .split("/")
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length >= 3) {
      return {
        brandTitle: parts[0],
        title: parts[1],
        variantTitle: parts.slice(2).join(" / "),
      };
    }

    if (parts.length === 2) {
      return {
        brandTitle: parts[0],
        title: parts[1],
        variantTitle: "",
      };
    }

    return {
      brandTitle: "",
      title: fallbackTitle || "Sample",
      variantTitle:
        variantTitle && variantTitle !== "Default Title" ? variantTitle : "",
    };
  }

  function getSampleLabel(sample) {
    if (!sample) return "Pick a sample";

    const name = [sample.brandTitle, sample.title].filter(Boolean).join(" - ");
    return sample.variantTitle
      ? `${name || sample.title || "Sample"} / ${sample.variantTitle}`
      : name || sample.title || "Sample";
  }

  function isSampleLine(item) {
    return Boolean(
      item &&
        (Number(item.product_id) === SAMPLE_PRODUCT_ID ||
          String(item.properties && item.properties[SAMPLE_PROPERTY]) === "true")
    );
  }

  function getQualifyingTotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items
      .filter((item) => {
        const type = String(item.product_type || "").toLowerCase();
        return !isSampleLine(item) && type !== "gift card" && item.handle !== "xmiles-plus";
      })
      .reduce((sum, item) => sum + Number(item.final_line_price || item.line_price || 0), 0);
  }

  function getCardInfo(card) {
    const image = card.querySelector(".xmiles-free-samples__image");
    const parsed = parseSampleVariantTitle(
      card.dataset.fullVariantTitle || card.dataset.variantTitle,
      card.dataset.productTitle
    );

    return {
      id: String(card.dataset.variantId),
      brandTitle: card.dataset.brandTitle || parsed.brandTitle,
      title: card.dataset.productTitle || parsed.title || "Sample",
      variantTitle: card.dataset.variantTitle || parsed.variantTitle,
      imageUrl: card.dataset.variantImageUrl || (image && image.currentSrc) || (image && image.src) || "",
    };
  }

  function initSection(section) {
    if (!section || section.dataset.freeSamplesReady === "true") return;
    section.dataset.freeSamplesReady = "true";

    const configEl = section.querySelector("[data-free-samples-config]");
    if (!configEl) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (error) {
      console.error("Free samples config could not be parsed", error);
      return;
    }

    const root = getCartRoot(config);
    const maxSamples = Math.min(Number(config.maxSamples || 3), 3);
    const threshold = Number(config.threshold || 4000);
    const moneyFormat = config.moneyFormat || "\u00a3{{amount}}";
    const cards = Array.from(section.querySelectorAll("[data-sample-card]"));
    const scroller = section.querySelector("[data-sample-scroller]");
    const prevButton = section.querySelector("[data-sample-scroll-prev]");
    const nextButton = section.querySelector("[data-sample-scroll-next]");
    const submitButtons = Array.from(section.querySelectorAll("[data-sample-submit]"));
    const selectedCount = section.querySelector("[data-selected-count]");
    const slots = Array.from(section.querySelectorAll("[data-sample-slot]"));
    const message = section.querySelector("[data-sample-message]");
    const thresholdMessage = section.querySelector("[data-sample-threshold-message]");
    const progress = section.querySelector("[data-sample-progress]");
    const trayStatus = section.querySelector("[data-sample-tray-status]");

    const state = {
      busy: false,
      cart: null,
      selected: new Map(),
      qualifyingTotal: 0,
    };

    function setMessage(text, type) {
      if (!message) return;
      message.textContent = text || "";
      message.classList.toggle("is-error", type === "error");
      message.classList.toggle("is-success", type === "success");
    }

    async function request(path, options) {
      const response = await fetch(endpoint(root, path), options);
      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(body.description || body.message || "Cart update failed");
      }

      return body;
    }

    async function fetchCart() {
      state.cart = await request("cart.js");
      state.qualifyingTotal = getQualifyingTotal(state.cart);
      return state.cart;
    }

    function getSampleItems() {
      return state.cart && Array.isArray(state.cart.items)
        ? state.cart.items.filter(isSampleLine)
        : [];
    }

    function syncSelectionFromCart() {
      state.selected.clear();
      getSampleItems()
        .slice(0, maxSamples)
        .forEach((item) => {
          const id = String(item.variant_id);
          const parsed = parseSampleVariantTitle(
            item.variant_title,
            item.product_title || "Sample"
          );

          state.selected.set(id, {
            id,
            brandTitle: parsed.brandTitle,
            title: parsed.title || item.product_title || "Sample",
            variantTitle: parsed.variantTitle,
            imageUrl: item.image || "",
          });
        });
    }

    function updateSlotImage(slot, sample) {
      const number = slot.querySelector("[data-sample-slot-number]");
      const image = slot.querySelector("[data-sample-slot-image]");
      const hasImage = Boolean(sample && sample.imageUrl);

      slot.classList.toggle("has-image", hasImage);

      if (image) {
        if (hasImage) {
          image.src = sample.imageUrl;
          image.alt = sample.title || "Selected sample";
        } else {
          image.removeAttribute("src");
          image.alt = "";
        }

        image.toggleAttribute("hidden", !hasImage);
      }

      if (number) {
        number.removeAttribute("hidden");
      }
    }

    function getScrollAmount() {
      const firstCard = cards[0];
      if (!firstCard) return 280;

      const gap = parseFloat(window.getComputedStyle(scroller).columnGap || "0");
      return firstCard.getBoundingClientRect().width + gap;
    }

    function updateScrollControls() {
      if (!scroller || !prevButton || !nextButton) return;

      const maxScroll = scroller.scrollWidth - scroller.clientWidth - 2;
      const hasOverflow = maxScroll > 0;
      const isAtStart = scroller.scrollLeft <= 2;
      const isAtEnd = scroller.scrollLeft >= maxScroll;

      prevButton.disabled = !hasOverflow || isAtStart;
      nextButton.disabled = !hasOverflow || isAtEnd;
      section.classList.toggle("has-sample-overflow", hasOverflow);
      scroller.classList.toggle("is-overflowing", hasOverflow);
    }

    async function changeLine(item, quantity) {
      return request("cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: item.key,
          quantity,
        }),
      });
    }

    async function normalizeCartSamples() {
      const sampleItems = getSampleItems();
      if (!sampleItems.length) return false;

      const changes = [];

      sampleItems.forEach((item, index) => {
        if (index >= maxSamples) {
          changes.push({ item, quantity: 0 });
        } else if (Number(item.quantity) > 1) {
          changes.push({ item, quantity: 1 });
        }
      });

      if (!changes.length) return false;

      for (const change of changes) {
        await changeLine(change.item, change.quantity);
      }

      await fetchCart();
      syncSelectionFromCart();
      return true;
    }

    function updateUi() {
      const selected = Array.from(state.selected.values()).slice(0, maxSamples);
      const isUnlocked = state.qualifyingTotal >= threshold;
      const remaining = Math.max(threshold - state.qualifyingTotal, 0);
      const progressValue = Math.min((state.qualifyingTotal / threshold) * 100, 100);
      const isComplete = state.selected.size >= maxSamples;

      section.classList.toggle("is-sample-complete", isComplete);

      cards.forEach((card) => {
        const id = String(card.dataset.variantId);
        const selectedHere = state.selected.has(id);
        const maxed = state.selected.size >= maxSamples && !selectedHere;
        const unavailable = card.classList.contains("is-unavailable");

        card.classList.toggle("is-selected", selectedHere);
        card.classList.toggle("is-maxed", maxed);

        card.querySelectorAll("[data-sample-toggle]").forEach((button) => {
          button.setAttribute("aria-pressed", selectedHere ? "true" : "false");
          button.disabled = (unavailable && !selectedHere) || maxed;
          if (button.classList.contains("xmiles-free-samples__select")) {
            button.textContent = selectedHere ? "Selected" : unavailable ? "Sold out" : "Add sample";
          }
        });
      });

      if (selectedCount) {
        selectedCount.textContent = String(Math.min(state.selected.size, maxSamples));
      }

      if (progress) {
        progress.textContent =
          state.selected.size === 1
            ? "1 sample selected"
            : `${Math.min(state.selected.size, maxSamples)} samples selected`;
      }

      if (trayStatus) {
        if (isComplete && isUnlocked) {
          trayStatus.textContent = "Ready to add";
        } else if (isComplete) {
          trayStatus.textContent = `${formatMoney(remaining, moneyFormat)} from free`;
        } else {
          trayStatus.textContent = `Choose ${maxSamples - state.selected.size} more`;
        }
      }

      slots.forEach((slot, index) => {
        const sample = selected[index];
        const label = slot.querySelector("p");

        slot.classList.toggle("is-filled", Boolean(sample));
        if (label) {
          label.textContent = getSampleLabel(sample);
        }

        updateSlotImage(slot, sample);
      });

      if (thresholdMessage) {
        thresholdMessage.textContent = isUnlocked
          ? "Free samples unlocked for this cart"
          : `Add ${formatMoney(remaining, moneyFormat)} more to make these free`;
      }

      submitButtons.forEach((submit) => {
        submit.hidden = !isComplete;
        submit.disabled = state.busy || !isComplete;

        if (state.busy) {
          submit.textContent = "Updating...";
        } else if (!isComplete) {
          submit.textContent = `Choose ${maxSamples}`;
        } else if (!isUnlocked) {
          submit.textContent = `Add to cart - ${formatMoney(remaining, moneyFormat)} from free`;
        } else {
          submit.textContent = "Add samples to cart";
        }
      });
    }

    async function submitSamples() {
      if (state.busy) return;

      state.busy = true;
      setMessage("", "");
      updateUi();

      try {
        await fetchCart();
        await normalizeCartSamples();

        const desiredIds = Array.from(state.selected.keys()).slice(0, maxSamples);
        const currentSampleItems = getSampleItems();
        const currentIds = currentSampleItems.map((item) => String(item.variant_id));

        for (const item of currentSampleItems) {
          if (!desiredIds.includes(String(item.variant_id))) {
            await changeLine(item, 0);
          } else if (Number(item.quantity) > 1) {
            await changeLine(item, 1);
          }
        }

        const missingIds = desiredIds.filter((id) => !currentIds.includes(id));

        if (missingIds.length) {
          await request("cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              items: missingIds.map((id) => ({
                id: Number(id),
                quantity: 1,
                properties: {
                  [SAMPLE_PROPERTY]: "true",
                  [OFFER_PROPERTY]: "try-it",
                  _sample_offer_limit: String(maxSamples),
                  _sample_offer_threshold: String(threshold),
                },
              })),
            }),
          });
        }

        await fetchCart();
        await normalizeCartSamples();
        syncSelectionFromCart();
        setMessage(
          state.qualifyingTotal >= threshold
            ? "Samples added to your cart."
            : `Samples added. Add ${formatMoney(threshold - state.qualifyingTotal, moneyFormat)} more to make them free.`,
          "success"
        );
        document.dispatchEvent(new CustomEvent("cart:drawer:open"));
      } catch (error) {
        console.error(error);
        setMessage(error.message || "We could not update your samples.", "error");
      } finally {
        state.busy = false;
        updateUi();
      }
    }

    cards.forEach((card) => {
      card.querySelectorAll("[data-sample-toggle]").forEach((button) => {
        button.addEventListener("click", () => {
          const info = getCardInfo(card);
          const selectedHere = state.selected.has(info.id);
          if (!info.id || (card.classList.contains("is-unavailable") && !selectedHere)) return;

          if (selectedHere) {
            state.selected.delete(info.id);
          } else if (state.selected.size < maxSamples) {
            state.selected.set(info.id, info);
          }

          setMessage("", "");
          updateUi();
        });
      });
    });

    submitButtons.forEach((submit) => {
      submit.addEventListener("click", submitSamples);
    });

    if (scroller && prevButton && nextButton) {
      prevButton.addEventListener("click", () => {
        scroller.scrollBy({ left: -getScrollAmount(), behavior: "smooth" });
      });

      nextButton.addEventListener("click", () => {
        scroller.scrollBy({ left: getScrollAmount(), behavior: "smooth" });
      });

      scroller.addEventListener("scroll", updateScrollControls, { passive: true });
      window.addEventListener("resize", updateScrollControls);
      updateScrollControls();
    }

    fetchCart()
      .then(() => normalizeCartSamples())
      .then(() => {
        syncSelectionFromCart();
        updateUi();
        updateScrollControls();
      })
      .catch((error) => {
        console.error(error);
        setMessage("We could not read your cart. Please refresh and try again.", "error");
        updateUi();
      });
  }

  function initAll(root) {
    const scope = root || document;
    scope.querySelectorAll("[data-free-samples-section]").forEach(initSection);
  }

  document.addEventListener("DOMContentLoaded", () => initAll(document));
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));

  initAll(document);
})();
